import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Package,
  Receipt,
  MapPin,
  Wallet,
  Truck,
  ExternalLink,
} from "lucide-react";
import ExcelJS from "exceljs";
import iziToast from "izitoast";
import PageWrapper from "components/PageWrapper";
import { getOrderDetailAPI } from "actions/ordersAction";
import { formatDate } from "utils/helper";
// import LoadingComponent from "components/LoadingComponent";
import Spinner from "components/Spinner";
import CartIDetailItemTable from "./CartIDetailItemTable";

// SmartBuy design-system status pills (order-detail-styles.css).
const STATUS_PILLS = {
  complete: { pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  backordered: { pill: "bg-s360-bg3 text-s360-fg2", dot: "bg-s360-fg3" },
  shipped: { pill: "bg-s360-info-bg text-[#002E4D]", dot: "bg-s360-info" },
  pending: { pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  cancelled: { pill: "bg-s360-danger-bg text-[#700A14]", dot: "bg-s360-danger" },
  other: { pill: "bg-s360-bg3 text-s360-fg2", dot: "bg-s360-fg3" },
};

const categorizeStatus = (status) => {
  const v = (status || "").toLowerCase();
  if (v.includes("complete") || v.includes("delivered")) return "complete";
  if (v.includes("cancel")) return "cancelled";
  if (v.includes("backorder")) return "backordered";
  if (v.includes("shipped")) return "shipped";
  if (
    v.includes("pending") ||
    v.includes("processing") ||
    v.includes("received") ||
    v.includes("submitted")
  )
    return "pending";
  return "other";
};

const OrderDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const distributor = state?.distributor || null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);

  const getOrderDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrderDetailAPI({ id, type: distributor });
      // console.log("OrderDetails--->", response);
      if (response.status) {
        setOrderDetail(response?.data);
      } else {
        setError(response?.message);
      }
    } catch (error) {
      console.log("error", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrderDetail();
  }, [id]);

  const formatCurrency = (amount, currency = "USD") => {
    if (currency === "USD") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    } else if (currency === "INR") {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(amount);
    }
  };

  const handleDownloadExcel = async () => {
    const lineItems = orderDetail?.data?.order_details || [];
    if (!lineItems.length) {
      iziToast.warning({
        title: "No data",
        message: "No order line items to download",
        position: "topCenter",
      });
      return;
    }

    const currency = orderDetail?.data?.currency_code || "";
    const orderRow = orderDetail?.data || {};
    console.log("orderRow", orderRow);
    const distributorName =
      distributor ||
      (orderRow?.api_partner) ||
      "";
    // TME/Rutronik/TI/Arrow/TTI/Avnet all come back through the same
    // order_id/po_number/invoice_number/tracking_number shape (see
    // OrdersController::formatArrowData/formatTtiData/formatAvnetData and
    // their TME/Rutronik/TI equivalents on the backend).
    const NO_LIVE_STATUS_DISTRIBUTORS = ["TME", "Rutronik", "TI", "Arrow", "TTI", "Avnet", "Verical"];
    const isNoLiveStatus = NO_LIVE_STATUS_DISTRIBUTORS.includes(distributor);

    let orderIdValue = "";
    if (distributor === "Mouser") {
      orderIdValue = orderRow.web_order_id;
    } else if (distributor === "Digikey") {
      orderIdValue = orderRow.sales_order_id;
    } else if (isNoLiveStatus) {
      orderIdValue = orderRow.order_id;
    } else {
      orderIdValue = id;
    }
    let poNumber = "";
    if (distributor === "Mouser") {
      poNumber = orderRow?.payment_detail?.PoNumber || "";
    } else if (distributor === "Digikey") {
      poNumber = orderRow?.payment_detail?.PoNumber || "";
    } else if (isNoLiveStatus) {
      poNumber = orderRow?.po_number || "";
    }
    // const invoice = orderRow?.invoice_number || orderRow?.invoice_id || "";
    let invoice = "";
    if (distributor === "Mouser") {
      invoice = orderRow?.delivery_detail?.TrackingDetails?.[0]?.Number || "";
    } else if (distributor === "Digikey") {
      invoice = orderRow?.invoice_id || "";
    } else if (isNoLiveStatus) {
      invoice = orderRow?.invoice_number || "";
    }
    let tracking = "";
    let trackingNumber = "";
    let shipMethod = "";
    if (distributor === "Mouser") {
      trackingNumber =
        orderRow?.delivery_detail?.TrackingDetails?.[0]?.Number || "";
      shipMethod = orderRow?.delivery_detail?.ShippingMethodName || "";
    } else if (distributor === "Digikey") {
      trackingNumber = orderRow?.tracking_number || "";
      shipMethod = orderRow?.delivery_detail?.ShippingMethodName || "";
    } else if (isNoLiveStatus) {
      trackingNumber = orderRow?.tracking_number || "";
    }
    tracking = trackingNumber && shipMethod
      ? `${trackingNumber} (${shipMethod})`
      : trackingNumber || shipMethod || "";
    const orderStatus =
      orderRow?.order_status_name || orderRow?.order_status || "";
    const orderedDateRaw =
      orderRow?.order_date || orderRow?.created_at || "";
    const orderedDate = orderedDateRaw ? formatDate(orderedDateRaw) : "";

    const headers = [
      "Distributor",
      "Order ID",
      "Ordered Date",
      "PO Number",
      "Invoice",
      "Tracking Number",
      "Order Status",
      "Part Number",
      "MFR Part Number",
      "Description",
      "Manufacturer",
      "Ordered Qty",
      "Shipped Qty",
      "Backordered Qty",
      "Shipped Total",
      "Unit Price",
      "Extended Price",
    ];

    const rows = lineItems.map((detail) => [
      distributorName,
      orderIdValue,
      orderedDate,
      poNumber,
      invoice,
      tracking,
      orderStatus,
      detail.mouser_part_number ?? detail.part_number ?? "",
      detail.mfr_part_number ?? "",
      detail.description ?? "",
      detail.manufacturer ?? "",
      detail.ordered_qty ?? detail.qty ?? 0,
      detail.shipped_qty ?? detail.qty ?? 0,
      detail.backordered_qty ?? 0,
      detail.shipped_total ?? 0,
      `${currency} ${detail.unit_price ?? 0}`.trim(),
      `${currency} ${detail.extended_price ?? 0}`.trim(),
    ]);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Order Details", {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    const headerRow = ws.addRow(headers);
    headerRow.font = { bold: true };
    ws.addRows(rows);

    const total = orderDetail?.data?.total ?? 0;
    const totalRow = ws.addRow([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Total",
      `${currency} ${Number(total).toFixed(2)}`.trim(),
    ]);
    totalRow.font = { bold: true };

    ws.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const len = cell.value ? String(cell.value).length : 0;
        if (len > maxLength) maxLength = len;
      });
      column.width = Math.min(maxLength + 2, 50);
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const orderRef =
      orderDetail?.data?.order_id ||
      orderDetail?.data?.web_order_id ||
      orderDetail?.data?.sales_order_id ||
      id;
    link.download = `order-${orderRef}-line-items.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  console.log("orderDetail=====>", { orderDetail });

  const d = orderDetail?.data;
  console.log("d=====>", d?.tracking_number);
  const ccy = d?.currency_code || "";
  const items = d?.order_details || [];
  const statusText = d?.order_status_name || d?.order_status || "N/A";
  const statusMeta = STATUS_PILLS[categorizeStatus(statusText)];
  const itemCount = items.length;
  const totalUnits = items.reduce(
    (s, it) => s + Number(it.ordered_qty ?? it.qty ?? 0),
    0,
  );
  const totalShipped = items.reduce(
    (s, it) => s + Number(it.shipped_qty ?? it.qty ?? 0),
    0,
  );
  const ship = d?.shipping_address;
  const bill = d?.billing_address;

  return (
    <PageWrapper>
      {/* {!orderDetail?.order_details && (
        <CartIDetailItemTable
        orderDetail={orderDetail}
        orderId={orderDetail?.order_id || orderDetail?.id}
        />
        )} */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-s360-fg3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 hover:text-s360-violet"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> My orders
            </button>
            <span className="opacity-50">/</span>
            <span className="font-mono">{d?.order_id}</span>
          </div>

          {/* Header card */}
          <div className="bg-white rounded-md shadow-sm px-[22px] py-[18px] grid grid-cols-[auto_1fr_auto] gap-[18px] items-center">
            <span className="w-11 h-11 rounded-lg bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center">
              <Package className="w-5 h-5" />
            </span>
            <div className="flex flex-col gap-1 min-w-0">
              <h1 className="font-mono font-bold text-[22px] leading-[26px] tracking-[-0.01em] text-s360-ink m-0">
                {d?.order_id || `#${id}`}
              </h1>
              <div className="flex gap-3 items-center flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${statusMeta.pill}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                  {statusText}
                </span>
                {distributor && (
                  <span className="text-xs text-s360-fg3">
                    <strong className="text-s360-fg1 font-semibold">{distributor}</strong> order
                  </span>
                )}
                {d?.sales_order_id && (
                  <span className="text-xs text-s360-fg3 font-mono">Sales #{d.sales_order_id}</span>
                )}
                {d?.web_order_id && (
                  <span className="text-xs text-s360-fg3 font-mono">Web #{d.web_order_id}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
              >
                <Download className="w-3.5 h-3.5" /> Download invoice
              </button>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Orders
              </button>
            </div>
          </div>

          {error && (
            <div className="text-s360-danger bg-s360-danger-bg border border-[#F1BBC0] rounded-md px-3 py-2.5 text-sm">
              {error}
            </div>
          )}

          {/* Line items card */}
          <div className="bg-white rounded-md shadow-sm overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 px-[18px] py-3.5 border-b border-s360-stroke1">
              <h3 className="text-base font-semibold text-s360-fg1 m-0">Line items</h3>
              <span className="ml-auto text-xs text-s360-fg3">
                <strong className="text-s360-fg1">{itemCount}</strong> lines ·{" "}
                <strong className="text-s360-fg1">{totalUnits.toLocaleString()}</strong> units ordered ·{" "}
                <strong
                  className={totalShipped < totalUnits ? "text-s360-warning" : "text-s360-fg1"}
                >
                  {totalShipped.toLocaleString()}
                </strong>{" "}
                shipped
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {[
                      { label: "Part number", right: false },
                      { label: "Description", right: false },
                      { label: "Manufacturer", right: false },
                      { label: "Ordered qty", right: true },
                      { label: "Shipped qty", right: true },
                      { label: "Backordered qty", right: true },
                      { label: "Shipped total", right: true },
                      { label: "Unit price", right: true },
                      { label: "Extended price", right: true },
                    ].map((col, i) => (
                      <th
                        key={i}
                        className={`py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${
                          col.right ? "text-right" : "text-left"
                        }`}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((detail, index) => (
                      <tr
                        key={index}
                        className="border-b border-s360-bg2 last:border-0 hover:bg-s360-stripe"
                      >
                        <td className="py-3.5 px-3.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono text-[13px] font-semibold text-s360-violet">
                              {detail.mouser_part_number ?? detail.part_number}
                            </span>
                            <span className="font-mono text-[11px] text-s360-fg3">
                              {detail.mfr_part_number}
                            </span>
                          </div>
                        </td>
                        <td className="py-3.5 px-3.5">
                          <div
                            className="max-w-[300px] truncate text-xs text-s360-fg2"
                            title={detail.description}
                          >
                            {detail.description}
                          </div>
                        </td>
                        <td className="py-3.5 px-3.5 text-[13px] text-s360-fg2">
                          {detail.manufacturer}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] font-semibold text-s360-fg1 tabular-nums">
                          {Number(detail.ordered_qty ?? detail.qty ?? 0).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] font-semibold text-s360-fg1 tabular-nums">
                          {Number(detail.shipped_qty ?? detail.qty ?? 0).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] font-semibold tabular-nums">
                          {(() => {
                            const back = Number(detail.backordered_qty ?? 0);
                            return (
                              <span className={back > 0 ? "text-s360-warning font-bold" : "text-s360-fg-disabled"}>
                                {back.toLocaleString()}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                          {detail.shipped_total ?? 0}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                          {ccy} {detail.unit_price}
                        </td>
                        <td className="py-3.5 px-3.5 text-right font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                          {ccy} {detail.extended_price}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-8 px-3 text-s360-fg3">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 px-[18px] py-4 border-t border-s360-stroke1 bg-gradient-to-b from-[#FBFAFF] to-s360-violet-50">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Merchandise
                </span>
                <span className="font-mono font-bold text-lg text-s360-ink tabular-nums">
                  {ccy} {Number(d?.merchandise_total ?? 0).toFixed(2)}
                </span>
              </div>
              {d?.tax_amount > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                    Tax
                  </span>
                  <span className="font-mono font-bold text-lg text-s360-info tabular-nums">
                    {ccy} {Number(d?.tax_amount ?? 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                  Total
                </span>
                <span className="font-mono font-bold text-[22px] leading-[26px] text-s360-success tabular-nums">
                  {ccy} {Number(d?.total ?? 0).toFixed(2)}
                </span>
              </div>
              {d?.ieccode && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                    IEC code
                  </span>
                  <span className="font-mono text-[13px] text-s360-violet">{d.ieccode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Info grid */}
          {(ship || bill || d?.payment_detail || d?.delivery_detail) && (
            <div className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_20px_55px_-35px_rgba(15,23,42,0.45)]">
              <div className="border-b border-slate-200/80 bg-gradient-to-r from-[#FBFAFF] to-[#F7FFFD] px-5 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-s360-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-s360-violet">
                      <Receipt className="h-3.5 w-3.5" />
                      Order information
                    </div>
                    <h3 className="mt-2 text-[18px] font-semibold text-slate-900">
                      Shipment and payment overview
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      A clearer snapshot of the order details, addresses, and delivery status.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-600 shadow-sm">
                    <span className="font-semibold text-slate-900">
                      {d?.order_status_name || "Processing"}
                    </span>
                    <span className="ml-2 text-slate-500">current status</span>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 border-b border-slate-200/80 bg-slate-50/70 p-4 sm:grid-cols-3">
                <div className="rounded-[18px] border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Buyer
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {d?.buyer_name || "—"}
                  </div>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    PO number
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 font-mono">
                    {d?.payment_detail?.PoNumber || "—"}
                  </div>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Tracking
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 font-mono">
                    {d?.tracking_number || d?.delivery_detail?.TrackingDetails?.[0]?.Number || "Pending"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="flex flex-col gap-3 rounded-[20px] border border-[#E3E8FF] bg-[#F8F9FF] p-4">
                  <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-s360-violet shadow-sm">
                    <Receipt className="h-3.5 w-3.5" /> Order details
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Sales order</span>
                      <span className="text-sm font-semibold text-slate-900 font-mono">
                        {d?.sales_order_id || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Web order</span>
                      <span className="text-sm font-semibold text-slate-900 font-mono">
                        {d?.web_order_id || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Status</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {d?.order_status_name || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Pending</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {d?.is_pending_order ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Scheduled</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {d?.is_scheduled ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </div>

                {ship && (
                  <div className="flex flex-col gap-3 rounded-[20px] border border-[#DCF8F2] bg-[#F7FFFD] p-4">
                    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0F766E] shadow-sm">
                      <MapPin className="h-3.5 w-3.5" /> Shipping address
                    </div>
                    <div className="text-sm leading-6 text-slate-600">
                      <span className="mb-1 block font-semibold text-slate-900">
                        {ship.first_name} {ship.last_name}
                      </span>
                      <div>{ship.company || "—"}</div>
                      <div>{ship.address_line_one || "—"}</div>
                      {ship.address_line_two && <div>{ship.address_line_two}</div>}
                      <div>
                        {[ship.city, ship.state_or_province, ship.postal_code]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </div>
                    </div>
                  </div>
                )}

                {bill && (
                  <div className="flex flex-col gap-3 rounded-[20px] border border-[#DCEBFF] bg-[#F8FBFF] p-4">
                    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-s360-info shadow-sm">
                      <MapPin className="h-3.5 w-3.5" /> Billing address
                    </div>
                    <div className="text-sm leading-6 text-slate-600">
                      <span className="mb-1 block font-semibold text-slate-900">
                        {bill.first_name} {bill.last_name}
                      </span>
                      <div>{bill.company || "—"}</div>
                      <div>{bill.address_line_one || "—"}</div>
                      {bill.address_line_two && <div>{bill.address_line_two}</div>}
                      <div>
                        {[bill.city, bill.state_or_province, bill.postal_code]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 rounded-[20px] border border-[#FFE8D7] bg-[#FFF9F4] p-4">
                  <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-s360-warning shadow-sm">
                    <Wallet className="h-3.5 w-3.5" /> Payment
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">PO number</span>
                      <span className="text-sm font-semibold text-slate-900 font-mono">
                        {d?.payment_detail?.PoNumber || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Method</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {d?.payment_detail?.PaymentMethodName || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 rounded-[20px] border border-[#DFF6E8] bg-[#F7FFF9] p-4">
                  <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-s360-success shadow-sm">
                    <Truck className="h-3.5 w-3.5" /> Delivery &amp; tracking
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500">Shipping method</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {d?.delivery_detail?.ShippingMethodName || "—"}
                      </span>
                    </div>
                    {(d?.delivery_detail?.TrackingDetails?.[0] || d?.tracking_number) && (
                      <>
                        <div className="flex flex-col">
                          <span className="text-[11px] text-slate-500">Tracking #</span>
                          <span className="text-sm font-semibold text-slate-900 font-mono">
                            {d?.delivery_detail?.TrackingDetails?.[0]?.Number || d?.tracking_number}
                          </span>
                        </div>

                        <div className="flex flex-col">
                          <span className="text-[11px] text-slate-500">Carrier link</span>

                          <a
                            href={
                              d?.delivery_detail?.TrackingDetails?.[0]?.Link ||
                              `https://mydhl.express.dhl/us/en/tracking.html#/results?id=${encodeURIComponent(
                                d?.tracking_number
                              )}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-semibold text-s360-violet hover:underline"
                          >
                            Track shipment <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
};

export default OrderDetailView;
